// src/services/apiService.js
import config from "../configs/config";

class ApiService {
    constructor() {
        this.baseURL = config.API_BASE_URL;
        this.token = null;
    }

    // Set authentication token
    setToken(token) {
        this.token = token;
        if (token) {
            localStorage.setItem('auth_token', token);
        } else {
            localStorage.removeItem('auth_token');
        }
    }

    // Get token from localStorage
    getToken() {
        if (!this.token) {
            this.token = localStorage.getItem('auth_token');
        }
        return this.token;
    }

    // Clear all auth data
    clearAuth() {
        this.token = null;
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
        localStorage.removeItem('organization');
    }

    // Generic request method
    async request(endpoint, options = {}) {
        const token = this.getToken();

        const headers = {
            'Content-Type': 'application/json',
            ...options.headers,
        };

        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        const configOptions = {
            ...options,
            headers,
        };

        try {
            const response = await fetch(`${this.baseURL}${endpoint}`, configOptions);

            // Handle unauthorized access
            if (response.status === 401) {
                this.clearAuth();
                window.location.href = '/login';
                throw new Error('Session expired. Please login again.');
            }

            const contentType = response.headers.get('content-type');
            let data;

            if (contentType && contentType.includes('application/json')) {
                data = await response.json();
            } else {
                data = await response.text();
            }

            if (!response.ok) {
                throw new Error(data.message || data || 'Request failed');
            }

            return { status: response.status, data };
        } catch (error) {
            console.error(`API Error (${endpoint}):`, error);
            throw error;
        }
    }

    // HTTP method helpers
    async post(endpoint, data, options = {}) {
        return this.request(endpoint, {
            method: 'POST',
            body: JSON.stringify(data),
            ...options,
        });
    }

    async get(endpoint, options = {}) {
        return this.request(endpoint, {
            method: 'GET',
            ...options,
        });
    }

    async put(endpoint, data, options = {}) {
        return this.request(endpoint, {
            method: 'PUT',
            body: JSON.stringify(data),
            ...options,
        });
    }

    async delete(endpoint, options = {}) {
        return this.request(endpoint, {
            method: 'DELETE',
            ...options,
        });
    }

    async loginWithMicrosoft(idToken) {
        return this.post('/loginOffice', {
            id_token: idToken,
        });
    }

    async logout() {
        try {
            const response = await this.post('/logout', {});
            this.clearAuth();
            return response;
        } catch (error) {
            this.clearAuth();
            throw error;
        }
    }

    // We'll add more APIs here as needed...
}

// Create a singleton instance
const apiService = new ApiService();

export default apiService;